import React from "react";
import {
  alpha,
  Box,
  Button,
  Paper,
  Stack,
  Typography,
  useTheme,
  Zoom,
} from "@mui/material";
import { Link } from "react-router-dom";
import EventIcon from "@mui/icons-material/Event";

import type { RegistrationPhase } from "../../types/Tournoi";
import { useAppSelector } from "../../store";
import { getTournoi } from "../../features/tournoi/tournoiSlice";
import { resolveTournoiHome } from "../../utils/resolveTournoiHome";
import { getHomeRegistrationHeroPaperSx } from "./homeConstants";
import { HomeKeyFactsSection } from "./HomeKeyFactsSection";
import { HomePosterInteractive } from "./HomePosterInteractive";

export type HomePreOpenHeroProps = {
  dateOuvertureInscriptions: string | null;
  lieuDisplay: string | null;
  tournamentLabel: string;
  /** Phase affichée sur la même home (fermé public vs pré-inscription clubs). */
  registrationPhase: Extract<RegistrationPhase, "closed" | "preRegistration">;
};

/**
 * Hero pré-ouverture : message d’ouverture, faits saillants lisibles (hors image),
 * affiche interactive (lightbox mobile + léger zoom au survol sur desktop).
 */
export const HomePreOpenHero: React.FC<HomePreOpenHeroProps> = ({
  dateOuvertureInscriptions,
  lieuDisplay,
  tournamentLabel,
  registrationPhase,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const tournoi = useAppSelector(getTournoi);
  const home = resolveTournoiHome(tournoi);

  const lieuLine =
    lieuDisplay?.trim() ||
    "Gymnase des Droits de l’Homme, Guyancourt (78)";

  return (
    <>
      <Zoom in>
        <Paper elevation={0} sx={getHomeRegistrationHeroPaperSx(theme)}>
          <Stack spacing={{ xs: 3, sm: 3.25 }}>
            <Box sx={{ textAlign: "center", px: { xs: 0.5, sm: 1 } }}>
              <Typography
                variant="overline"
                sx={{
                  display: "block",
                  fontWeight: 700,
                  letterSpacing: "0.16em",
                  color: isDark
                    ? theme.palette.grey[200]
                    : theme.palette.primary.dark,
                  mb: 1,
                }}
              >
                {tournamentLabel}
              </Typography>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                spacing={1}
                sx={{ mb: 0.5 }}
              >
                <EventIcon
                  sx={{
                    fontSize: 30,
                    color: isDark
                      ? theme.palette.grey[200]
                      : theme.palette.primary.main,
                  }}
                />
                <Typography
                  variant="h6"
                  component="h1"
                  sx={{
                    fontWeight: 800,
                    letterSpacing: "-0.02em",
                    color: "text.primary",
                  }}
                >
                  Ouverture des inscriptions
                </Typography>
              </Stack>
              {dateOuvertureInscriptions ? (
                <Typography
                  variant="h4"
                  component="p"
                  sx={{
                    m: 0,
                    mt: 0.5,
                    fontWeight: 800,
                    color: isDark
                      ? theme.palette.secondary.light
                      : theme.palette.primary.dark,
                    fontSize: { xs: "1.5rem", sm: "1.85rem" },
                    lineHeight: 1.3,
                  }}
                >
                  Le {dateOuvertureInscriptions} à{" "}
                  {home.registrationOpenTimeLabel}
                </Typography>
              ) : (
                <Typography
                  variant="body1"
                  sx={{
                    mt: 1,
                    fontStyle: "italic",
                    color: isDark
                      ? theme.palette.grey[300]
                      : theme.palette.text.secondary,
                  }}
                >
                  Date d&apos;ouverture à communiquer — revenez bientôt.
                </Typography>
              )}
            </Box>

            <Box
              sx={{
                px: { xs: 0.5, sm: 1.5 },
                py: { xs: 2, sm: 2.25 },
                borderRadius: 2,
                bgcolor: isDark
                  ? alpha(theme.palette.common.white, 0.06)
                  : alpha(theme.palette.primary.main, 0.06),
                border: `1px solid ${alpha(
                  theme.palette.secondary.main,
                  isDark ? 0.45 : 0.35
                )}`,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  textAlign: "center",
                  mb: 2,
                  lineHeight: 1.6,
                  color: isDark ? "grey.200" : "text.secondary",
                  fontWeight: 500,
                }}
              >
                {registrationPhase === "preRegistration"
                  ? "Les clubs organisateurs peuvent d’ores et déjà accéder au parcours d’inscription réservé."
                  : "Le jour J, l’inscription et le paiement sécurisé se font sur ce site — vous pouvez dès maintenant consulter la page d’inscription et les tableaux."}
              </Typography>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                justifyContent="center"
                alignItems="center"
              >
                <Button
                  component={Link}
                  to="/register"
                  variant="contained"
                  color="primary"
                  size="large"
                  sx={{
                    fontWeight: 800,
                    width: { xs: "100%", sm: "auto" },
                    minWidth: { sm: 280 },
                    py: 1.5,
                    px: 3,
                    fontSize: "1.05rem",
                    boxShadow: isDark ? 4 : 3,
                  }}
                >
                  {registrationPhase === "preRegistration"
                    ? "Accéder à la pré-inscription"
                    : "Page d’inscription"}
                </Button>
                <Button
                  component={Link}
                  to="/boards"
                  variant="outlined"
                  color="secondary"
                  size="large"
                  sx={{
                    fontWeight: 700,
                    width: { xs: "100%", sm: "auto" },
                    minWidth: { sm: 200 },
                    ...(isDark && {
                      borderColor: "secondary.main",
                      color: "secondary.light",
                      "&:hover": {
                        borderColor: "secondary.light",
                        bgcolor: alpha(theme.palette.secondary.main, 0.12),
                        color: "common.white",
                      },
                    }),
                  }}
                >
                  Voir les tableaux
                </Button>
              </Stack>
            </Box>

            <HomeKeyFactsSection
              lieuLine={lieuLine}
              weekendLine={home.weekendLine}
              dotationsLine={home.dotationsLine}
            />

            {home.posterUrl ? (
              <Box sx={{ px: { xs: 0.25, sm: 0 } }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    display: "block",
                    textAlign: "center",
                    fontWeight: 700,
                    letterSpacing: "0.14em",
                    fontSize: { xs: "0.8rem", sm: "0.85rem" },
                    color: isDark
                      ? theme.palette.secondary.light
                      : theme.palette.primary.dark,
                    mb: { xs: 1.25, sm: 1.5 },
                  }}
                >
                  Affiche officielle
                </Typography>
                <HomePosterInteractive
                  posterUrl={home.posterUrl}
                  posterAlt={home.posterAlt}
                />
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    textAlign: "center",
                    mt: { xs: 1.25, sm: 1.5 },
                    lineHeight: 1.55,
                    px: { xs: 1.5, sm: 2 },
                    color: isDark
                      ? theme.palette.grey[300]
                      : theme.palette.text.secondary,
                  }}
                >
                  En vue agrandie, faites défiler pour voir tout le programme en
                  taille réelle.
                </Typography>
              </Box>
            ) : null}
          </Stack>
        </Paper>
      </Zoom>
    </>
  );
};
